import { Box, Stack } from '@mui/material';
import React, { useState } from 'react';

import ArrayFilter from '../../molecules/ArrayFilter';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import IOption from '@models/option';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import JDatePicker from '@atoms/JDatePicker';
import { JTable } from '@atoms/JTable';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import TopUpsRow from './top-ups-row';
import { appendSearchFilter } from '@lib/tableFilterUtils';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useGetTopUps } from '@/modules/credits/queries';
import { useTranslation } from 'react-i18next';

const TABLE_COLS: TableColumn[] = [
  {
    name: 'ENTERPRISE',
    orderable: true,
    sortingField: 'creditTopup.enterprise.name',
  },
  {
    name: 'AMOUNT_€',
    orderable: true,
    sortingField: 'creditTopup.eurosAmount',
  },
  {
    name: 'TOP_UP_JOBCOINS',
    orderable: true,
    sortingField: 'creditTopup.creditsAmount',
  },
  {
    name: 'ESTABLISHMENTS',
    orderable: false,
    sortingField: 'creditTopup.establishmentsCount',
  },
  {
    name: 'DATE_TOP_UP',
    orderable: true,
    sortingField: 'creditTopup.createdAt',
  },
];

const TOP_UPS_PER_PAGE = 20;
const DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'creditTopup.createdAt',
};

const TopUpsList: React.FC = () => {
  const { t } = useTranslation();

  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(DEFAULT_ORDER);
  const [page, setPage] = useState<number>();

  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');

  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);

  const { data: dataTopUps } = useGetTopUps(
    {
      page,
      limit: TOP_UPS_PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );

  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );

  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  const meta = dataTopUps?.data.meta;

  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JAutocomplete
          value={{ label: enterpriseInputValue, value: '' }}
          label={t('ENTERPRISE_NAME')}
          options={enterprisesOptions}
          onChange={(ev, newValue) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_ENTERPRISE,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            );
            setEnterpriseInputValue((newValue as IOption)?.label || '');
          }}
          onTextChange={(val) => setEnterpriseInputValue(val)}
          whiteBg
        />
        <ArrayFilter
          onChangeMin={(value) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_AMOUNT_MIN,
              value.toString(),
              searchFilter,
              setSearchFilter,
            );
          }}
          onChangeMax={(value) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_AMOUNT_MAX,
              value.toString(),
              searchFilter,
              setSearchFilter,
            );
          }}
          labelFilter={t('ENTER_MAX_MIN_AMOUNT')}
          labelInputEmpty={t('AMOUNT')}
          symbol={'€'}
        />
        <ArrayFilter
          onChangeMin={(value) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_CREDIT_MIN,
              value.toString(),
              searchFilter,
              setSearchFilter,
            );
          }}
          onChangeMax={(value) => {
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_CREDIT_MAX,
              value.toString(),
              searchFilter,
              setSearchFilter,
            );
          }}
          labelFilter={t('ENTER_MAX_MIN_CREDIT')}
          labelInputEmpty={t('CREDIT')}
        />

        <JDatePicker
          onClear={() =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_CREATED_AT_FROM,
              '',
              searchFilter,
              setSearchFilter,
            )
          }
          handleChange={(value) =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_CREATED_AT_FROM,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
          label={t('START_DATE')}
          whiteBg
        />
        <JDatePicker
          onClear={() =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_CREATED_AT_TO,
              '',
              searchFilter,
              setSearchFilter,
            )
          }
          handleChange={(value) =>
            appendSearchFilter(
              FilterCriteriaFields.CREDIT_TOPUP_CREATED_AT_TO,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
          label={t('END_DATE')}
          whiteBg
        />
      </Stack>
      <JTable
        isNotFound={dataTopUps?.data.meta.itemCount === 0}
        columns={TABLE_COLS}
        notFoundLabel={t('NO_TOP_UPS_FOUND') ?? ''}
        total={dataTopUps?.data.meta.itemCount}
        perpage={TOP_UPS_PER_PAGE}
        nbpages={dataTopUps?.data.meta.pageCount}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {dataTopUps?.data.data.map((topUp) => (
          <TopUpsRow row={topUp} key={topUp.creditTopup_id} />
        ))}
      </JTable>
      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default TopUpsList;
